/**
 * Adjustment Rules — Ani's v0 step 5.
 *
 * Her mockup presents each rule as a sentence with a switch: read what it does, check it against the
 * contract, turn off the ones that don't apply. The typed-form card is the wrong shape for that — the
 * question here isn't "is `multiplier` 0.85?", it's "does this contract really pay the lesser of 85%
 * of charges?", and you answer that by reading a sentence.
 *
 * **The switch is the existing accept/reject verdict, not a new concept.** On = accepted, off =
 * rejected, and it stays reversible. Introducing a separate "enabled" flag would have meant two ways
 * to say a rule doesn't apply, which then disagree — the Build checklist reads the verdict, so a rule
 * switched off but still `accepted` would be dutifully built by someone downstream.
 *
 * The logic sentence is DERIVED from the construct's own config (`ruleSummary.ts`), never authored, so
 * it cannot drift from what will actually be built. Editing remains in the typed form below.
 */

import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import React from "react";
import styled from "styled-components";

import { IDraftRecommendation } from "../../../store/queries/contractModelDrafts";
import { confidenceSeverity } from "../utils";
import {
  describeRuleLogic,
  ruleAppliesTo,
  ruleKindLabel,
  ruleSource,
  ruleTitle,
} from "./ruleSummary";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const RuleCard = styled.div<{ $off: boolean }>`
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  padding: 1rem;
  background: var(--surface-card);
  /* A switched-off rule stays readable but visibly inert — you must be able to see what you turned off. */
  opacity: ${({ $off }) => ($off ? 0.55 : 1)};
`;

const Logic = styled.p`
  margin: 0.75rem 0 0;
  padding: 0.6rem 0.75rem;
  background: var(--surface-100);
  border-radius: 4px;
  font-size: 0.9rem;
  line-height: 1.45;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 0.75rem;
  font-size: 0.85rem;
`;

const MetaLabel = styled.span`
  color: var(--text-color-secondary);
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

interface IAdjustmentRulesPanelProps {
  recs: IDraftRecommendation[];
  busy: boolean;
  onAccept: (rec: IDraftRecommendation) => void;
  onReject: (rec: IDraftRecommendation) => void;
  /** Opens the full typed form for a rule whose fields need correcting. */
  renderDetail: (rec: IDraftRecommendation) => React.ReactNode;
  focusRecId?: string | null;
}

function AdjustmentRulesPanel({
  recs,
  busy,
  onAccept,
  onReject,
  renderDetail,
  focusRecId,
}: IAdjustmentRulesPanelProps) {
  const [openId, setOpenId] = React.useState<string | null>(focusRecId ?? null);

  if (recs.length === 0) return null;
  const active = recs.filter((r) => r.status !== "rejected").length;

  return (
    <Wrap>
      <div className="flex align-items-center gap-2">
        <i className="pi pi-shield text-color-secondary" />
        <span className="text-sm text-color-secondary">
          {active} of {recs.length} rules active
        </span>
      </div>

      {recs.map((rec) => {
        const off = rec.status === "rejected";
        const accepted = rec.status === "accepted";
        const isOpen = openId === rec.id;
        return (
          <RuleCard key={rec.id} $off={off}>
            <div className="flex align-items-start gap-3">
              <div className="flex-1">
                <div className="flex align-items-center gap-2 flex-wrap">
                  <span className="font-semibold">{ruleTitle(rec)}</span>
                  <Tag
                    className="font-normal"
                    severity="info"
                    value={ruleKindLabel(rec)}
                  />
                  <Tag
                    className="font-normal"
                    severity={confidenceSeverity(rec.confidence)}
                    value={`${rec.confidence} confidence`}
                  />
                  {rec.status === "needs_input" ? (
                    <Tag severity="danger" value="Needs input" />
                  ) : null}
                </div>
                {ruleSource(rec) ? (
                  <div className="text-sm text-color-secondary mt-1">
                    Source: <em>{ruleSource(rec)}</em>
                  </div>
                ) : null}
              </div>
            </div>

            <Logic>{describeRuleLogic(rec)}</Logic>

            <Meta>
              <div>
                <MetaLabel>Applies to</MetaLabel>
                <span>{ruleAppliesTo(rec)}</span>
              </div>
              <div>
                <MetaLabel>Status</MetaLabel>
                <span>
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {off
                    ? "Off — will not be built"
                    : accepted
                    ? "Confirmed"
                    : "Not yet confirmed"}
                </span>
              </div>
            </Meta>

            <Button
              type="button"
              className="p-button-text p-button-sm p-0 mt-2"
              icon={isOpen ? "pi pi-chevron-down" : "pi pi-chevron-right"}
              label={isOpen ? "Hide fields" : "Edit fields"}
              onClick={() => setOpenId(isOpen ? null : rec.id)}
            />

            {isOpen ? (
              // Expanded: the full form owns the verdict, since only it can save an inline edit.
              <div className="mt-2">{renderDetail(rec)}</div>
            ) : (
              // Collapsed: Confirm/Reject right here, so you never have to open the form just to
              // register a verdict. Same selected-state styling as the full card's buttons.
              <div className="flex gap-2 mt-3 align-items-center">
                <Button
                  type="button"
                  label={accepted ? "Confirmed" : "Confirm"}
                  icon={accepted ? "pi pi-check-circle" : "pi pi-check"}
                  className={`p-button-sm ${
                    accepted ? "p-button-success" : ""
                  }`}
                  disabled={busy}
                  onClick={() => onAccept(rec)}
                />
                <Button
                  type="button"
                  label={off ? "Rejected" : "Reject"}
                  icon={off ? "pi pi-times-circle" : undefined}
                  className={`p-button-sm p-button-danger ${
                    off ? "" : "p-button-outlined"
                  }`}
                  disabled={busy}
                  onClick={() => onReject(rec)}
                />
              </div>
            )}
          </RuleCard>
        );
      })}
    </Wrap>
  );
}

export default AdjustmentRulesPanel;
